import { jsonCodec, keyOrDie, mapValues, z, type DeepReadonly, type Serializable } from "@repo/utils";
import { untrack } from "svelte";

const usedKeys = new Set();

export function useLocalStorage<S extends Record<string, z.core.$ZodType<unknown, Serializable>>>(
    prefix: string,
    shape: S,
) {
    function key(x: string) {
        return `${prefix}:${x}`;
    }

    if ($effect.tracking()) {
        $effect(() => {
            for (const k of Object.keys(shape)) {
                const kk = key(k);
                if (usedKeys.has(kk)) {
                    throw new Error(`Local storage key ${kk} has been used from multiple places simultaneously`);
                }
                usedKeys.add(kk);
            }
            return () => {
                for (const k of Object.keys(shape)) {
                    usedKeys.delete(key(k));
                }
            };
        });
    }

    const codecs = mapValues(shape, (_, v) => jsonCodec(v));
    const tickers = $state(mapValues(shape, () => 0)) as Record<string, number>;

    return new Proxy(
        {} as {
            [K in keyof z.output<z.ZodMiniObject<S>>]?: DeepReadonly<z.output<z.ZodMiniObject<S>>[K]>;
        },
        {
            get(_, k: string) {
                void tickers[k];
                const raw = localStorage.getItem(key(k));
                if (raw !== null) {
                    const codec = keyOrDie(codecs, k);
                    const decoded = z.safeDecode(codec, raw);
                    if (decoded.success) {
                        return decoded.data;
                    }
                    localStorage.removeItem(key(k));
                }

                return undefined;
            },
            set(_, k: string, value) {
                if (value === undefined) {
                    localStorage.removeItem(key(k));
                } else {
                    const codec = keyOrDie(codecs, k);
                    localStorage.setItem(key(k), z.encode(codec, value));
                }
                untrack(() => (tickers[k] = (tickers[k] ?? 0) + 1));
                return true;
            },
            deleteProperty(_, k: string) {
                localStorage.removeItem(key(k));
                untrack(() => (tickers[k] = (tickers[k] ?? 0) + 1));
                return true;
            },
        },
    );
}

export const debug = useLocalStorage("debug", { enabled: z.boolean() });
